import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import Button from "../Button/Button"
import THEME from "../../configuration/Theme"
import { AnimationTypes } from "../Animate/Animate"
import { usePayment } from "../../hooks/Payment/usePayment"
import { SubscriptionPurchaseResult } from "../../Objects/Company/SubscriptionPurchaseResult"
import { SubscriptionPurchaseStatus } from "../../Objects/Company/SubscriptionPurchaseStatus"

interface Props {
  purchase: SubscriptionPurchaseResult
  onConfirmed: () => void
}

const POLL_INTERVAL_MS = 15000

export const PixPaymentPanel = ({ purchase, onConfirmed }: Props) => {
  const { t } = useTranslation("frontend")

  const { getSubscriptionPurchaseStatus } = usePayment()

  const [copied, setCopied] = useState(false)

  const [confirmed, setConfirmed] = useState(false)

  const onConfirmedRef = useRef(onConfirmed)

  useEffect(() => {
    onConfirmedRef.current = onConfirmed
  }, [onConfirmed])

  useEffect(() => {
    if (confirmed) return

    const interval = setInterval(async () => {
      await getSubscriptionPurchaseStatus(purchase.chargeId, (status: SubscriptionPurchaseStatus) => {
        if (status.isApproved) {
          setConfirmed(true)
          onConfirmedRef.current()
        }
      })
    }, POLL_INTERVAL_MS)

    return () => clearInterval(interval)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchase.chargeId, confirmed])

  const handleCopy = async () => {
    if (!purchase.pixQrCode) return

    await navigator.clipboard.writeText(purchase.pixQrCode)

    setCopied(true)

    setTimeout(() => setCopied(false), 3000)
  }

  if (confirmed) {
    return (
      <div className="rounded-xl ring-2 ring-green-300 bg-green-50 p-6 text-center">
        <p className="font-black text-green-700 text-lg">{t("my_company.pix_payment_confirmed_title")}</p>
        <p className="text-sm text-green-600 mt-1">{t("my_company.pix_payment_confirmed_description")}</p>
      </div>
    )
  }

  return (
    <div className="rounded-xl ring-2 ring-orange-300 bg-white p-6">
      <h4 className="font-black text-gray-800 text-lg">{t("my_company.pix_panel_title")}</h4>

      <p className="text-sm text-gray-500 mt-1">{t("my_company.pix_panel_description")}</p>

      {purchase.pixQrCodeBase64 && (
        <div className="flex justify-center mt-4">
          <img
            src={`data:image/png;base64,${purchase.pixQrCodeBase64}`}
            alt="QR Code Pix"
            className="w-48 h-48 rounded-lg ring-1 ring-gray-200"
          />
        </div>
      )}

      {purchase.pixQrCode && (
        <div className="mt-4">
          <div className="break-all rounded-lg bg-gray-50 ring-1 ring-gray-200 p-3 text-xs text-gray-600">
            {purchase.pixQrCode}
          </div>

          <div className="mt-3">
            <Button
              type="primary"
              typeButton="button"
              size="medium"
              label={copied ? t("my_company.pix_copy_success") : t("my_company.pix_copy_code")}
              bgColor={THEME.colors.buttonBGSaveColor}
              animationTypes={AnimationTypes.fadeIn}
              onClick={handleCopy}
              descriptionKey="interactionDescriptions:components.payment.pixPaymentPanel.button.copy"
            />
          </div>
        </div>
      )}

      {purchase.pixExpiresAtUtc && (
        <p className="text-xs text-gray-400 mt-3">
          {t("my_company.pix_expires_at", { date: purchase.pixExpiresAtUtc.toString().toReadableDate() })}
        </p>
      )}

      <div className="flex items-center gap-2 mt-4 text-sm text-gray-500">
        <span className="inline-block h-2 w-2 rounded-full bg-orange-400 animate-pulse" />
        {t("my_company.pix_waiting_confirmation")}
      </div>

      {purchase.initPointUrl && (
        <div className="mt-4 border-t border-gray-100 pt-3">
          <a
            className="text-sm font-bold text-blue-700 hover:underline"
            href={purchase.initPointUrl}
            target="_blank"
            rel="noreferrer"
            data-interaction-description-key="interactionDescriptions:components.payment.pixPaymentPanel.link.checkoutFallback"
          >
            {t("my_company.redirect_button")}
          </a>
        </div>
      )}
    </div>
  )
}
